import React, { useLayoutEffect, useState } from 'react';

const VIEWPORT_MARGIN = 16;
const TARGET_GAP = 16;
const NARROW_BREAKPOINT = 768;

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

export const calculatePopoverPosition = ({
    targetRect,
    dialogRect,
    viewportWidth,
    viewportHeight,
    placement = 'right',
}) => {
    const availableWidth = Math.max(viewportWidth - (VIEWPORT_MARGIN * 2), 0);
    const availableHeight = Math.max(viewportHeight - (VIEWPORT_MARGIN * 2), 0);
    const dialogWidth = Math.min(dialogRect.width, availableWidth);
    const dialogHeight = Math.min(dialogRect.height, availableHeight);
    let left;
    let top;

    if (placement === 'left') {
        left = targetRect.left - dialogWidth - TARGET_GAP;
        if (left < VIEWPORT_MARGIN) left = targetRect.right + TARGET_GAP;
        top = targetRect.top;
    } else if (placement === 'bottom') {
        left = targetRect.left;
        top = targetRect.bottom + TARGET_GAP;
        if (top + dialogHeight > viewportHeight - VIEWPORT_MARGIN) {
            top = targetRect.top - dialogHeight - TARGET_GAP;
        }
    } else {
        left = targetRect.right + TARGET_GAP;
        if (left + dialogWidth > viewportWidth - VIEWPORT_MARGIN) {
            left = targetRect.left - dialogWidth - TARGET_GAP;
        }
        top = targetRect.top;
    }

    return {
        left: clamp(left, VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, viewportWidth - dialogWidth - VIEWPORT_MARGIN)),
        top: clamp(top, VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, viewportHeight - dialogHeight - VIEWPORT_MARGIN)),
    };
};

const HelpPopover = ({ children, dialogRef, placement, target }) => {
    const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
    const [position, setPosition] = useState(null);
    const [highlight, setHighlight] = useState(null);
    const isNarrow = viewportWidth < NARROW_BREAKPOINT;
    const hasTarget = Boolean(target?.isConnected && !isNarrow);

    useLayoutEffect(() => {
        const updateLayout = () => {
            setViewportWidth(window.innerWidth);

            if (!target?.isConnected || window.innerWidth < NARROW_BREAKPOINT) {
                setPosition(null);
                setHighlight(null);
                return;
            }

            const targetRect = target.getBoundingClientRect();
            const dialogRect = dialogRef.current?.getBoundingClientRect();
            if (!dialogRect || targetRect.width <= 0 || targetRect.height <= 0) {
                setPosition(null);
                setHighlight(null);
                return;
            }

            setHighlight({
                left: targetRect.left - 6,
                top: targetRect.top - 6,
                width: targetRect.width + 12,
                height: targetRect.height + 12,
            });
            setPosition(calculatePopoverPosition({
                targetRect,
                dialogRect,
                viewportWidth: window.innerWidth,
                viewportHeight: window.innerHeight,
                placement,
            }));
        };

        updateLayout();
        window.addEventListener('resize', updateLayout);
        window.addEventListener('scroll', updateLayout, true);
        return () => {
            window.removeEventListener('resize', updateLayout);
            window.removeEventListener('scroll', updateLayout, true);
        };
    }, [dialogRef, placement, target]);

    const presentation = isNarrow ? 'bottom-sheet' : hasTarget && position ? 'anchored' : 'centered';
    const dialogClasses = presentation === 'bottom-sheet'
        ? 'fixed inset-x-0 bottom-0 max-h-[88vh] overflow-y-auto rounded-t-2xl'
        : presentation === 'anchored'
            ? 'fixed max-h-[calc(100vh-2rem)] w-[min(24rem,calc(100vw-2rem))] overflow-y-auto rounded-2xl'
            : 'fixed left-1/2 top-1/2 max-h-[calc(100vh-2rem)] w-[min(24rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl';

    return (
        <div data-help-tour-root className="fixed inset-0 z-[100]">
            {highlight ? (
                <div
                    aria-hidden="true"
                    className="help-tour-motion pointer-events-none fixed z-[101] rounded-xl border-2 border-white ring-4 ring-indigo-400"
                    style={{
                        ...highlight,
                        boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.62)',
                    }}
                />
            ) : (
                <div aria-hidden="true" className="fixed inset-0 bg-slate-950/60" />
            )}
            <div
                ref={dialogRef}
                data-help-tour-dialog
                data-presentation={presentation}
                className={`help-tour-motion z-[102] border border-indigo-100 bg-white p-5 shadow-2xl sm:p-6 ${dialogClasses}`}
                style={presentation === 'anchored' ? position : undefined}
            >
                {children}
            </div>
        </div>
    );
};

export default HelpPopover;
